<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DLoading,
  InfoCircleIcon,
  Trash03Icon,
  DModal,
  DBtn,
  DTextAreaField,
  DSelectFieldSearch
} from 'front-library'
import {
  getProcessClosedPayroll,
  getProcessCountry,
  reversePayroll,
  DataReversePayroll
} from '@/app/core/services/reverse-payrolls'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const payrollStore = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const descriptionModal = ref('')

const loading = ref(false)

/* Datos */
const processes = ref('')
const searchProcesses = ref('')
const itemsProcesses = ref<string[]>([])
const itemsClosedPayrolls = ref<any[]>([])
const isShowData = ref(false)
const dataClosedPayroll = ref<any>({})
const dateCurrent = dayjs(dayjs()).format('YYYY-MM-DD')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})
const nameProcess = computed(() => {
  return itemsProcesses.value
    .filter((item: any) => item.id === processes.value)
    .map((_: any) => _.name)[0]
})

/* Esquema de validacion campos obligatorios */
const validationSchema = object().shape({
  observation: string()
    .required(t('form.valid.required'))
    .min(20, t('form.valid.minLength', [20]))
    .max(300, t('form.valid.maxLength', [300]))
})

/* Uso del metodo de validación, retorno de errores */
const { handleSubmit, errors } = useForm<DataReversePayroll>({
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: observation } = useField<string>(ref('observation'))
const { value: closedPayrollId } = useField<string>(ref('closedPayrollId'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit(values => {
  void sendReversePayroll(values)
})

/* Enviar reverso de nomina */
const sendReversePayroll = async (values: any) => {
  loading.value = true
  try {
    await reversePayroll(values)
    back()
    notifications.addNotification({
      title: t('notification.created'),
      text: t('reversePayrolls.notification.action', [
        t('notification.wasCreated')
      ]),
      type: 'success',
      time: 10000
    })
    loading.value = false
  } catch (error: any) {
    closeModal()
    notificationError(error, 'reversePayrolls', t, notifications, loading)
  }
}

const formatDate = computed(() => {
  return (date: any) => dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
})

/* Filtrar proceso */
const filterProcess = async () => {
  if (processes.value === '') return
  loading.value = true
  isShowData.value = false
  try {
    const codeProcess = itemsProcesses.value
      .filter((item: any) => item.id === processes.value)
      .map((_: any) => _.code)[0]

    itemsClosedPayrolls.value = await getProcessClosedPayroll(
      processes.value,
      codeProcess,
      payrollStore.payroll?.country?.iso_code
    )

    if (itemsClosedPayrolls.value.length === 0) isShowData.value = true
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'reversePayrolls', t, notifications, loading)
  }
}

/* Cambiar proceso */
const changeProcess = () => {
  itemsClosedPayrolls.value = []
  isShowData.value = false
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
  observation.value = ''
}

/* Abrir modal para reversar nomina */
const openModalReversePayroll = (data: any) => {
  dataClosedPayroll.value = data
  showModal.value = true
  closedPayrollId.value = dataClosedPayroll.value.id
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListReversePayrolls' })
}

onMounted(async () => {
  loading.value = true
  try {
    itemsProcesses.value = await getProcessCountry()
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'reversePayrolls', t, notifications, loading)
  }
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="t('loading.data')" />
    <DModal
      :open="showModal"
      :description="descriptionModal"
      :isFooter="false"
      :size="'w-1/4 md:w-2/5 lg:w-1/3 xl:w-1/4 2xl:w-1/4'"
      @closeModal="closeModal">
      <template v-slot:form>
        <div class="flex flex-col items-center gap-10">
          <section
            class="flex items-center justify-center rounded-full sm:mx-0 h-14 w-14 bg-red-100">
            <InfoCircleIcon class="h-8 w-8 text-red-500" />
          </section>
          <section class="px-5 text-center text-gray-400 text-sm">
            <h1
              class="text-black text-xl"
              v-html="t('reversePayrolls.create.titleModal')" />
            <p class="mt-3">
              {{ t('reversePayrolls.create.confirmation') }}
              <span class="text-gray-500 font-bold">{{ nameProcess }}</span>
              {{ t('reversePayrolls.create.datePrompt') }}
              <span class="text-gray-500 font-bold">
                {{ formatDate(dataClosedPayroll.start_date) }} -
                {{ formatDate(dataClosedPayroll.end_date) }}
              </span>
              {{ t('reversePayrolls.create.deleteWarning') }}
            </p>
            <div
              v-if="payrollStore.payroll?.country?.iso_code === 'MX' && dataClosedPayroll.stamped_payroll_id"
              class="p-4 mt-3 mb-4 text-sm rounded-lg bg-orange-100 text-orange-800 border border-orange-300"
            >
              <p class="font-bold mb-1">
                {{ t('reversePayrolls.notification.important') }}
              </p>
              <p>
                {{ t('reversePayrolls.create.deleteWarningStamped') }}
              </p>
            </div>
            <p
              class="mt-5"
              v-html="t('reversePayrolls.create.confirmationMessage')" />
            <DTextAreaField
              v-model="observation"
              :placeholder="t('reversePayrolls.fields.reason')"
              :error="errors.observation"
              :rows="7" />
          </section>
          <section class="flex gap-3">
            <button
              @click="closeModal"
              class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
              v-html="t('form.button.cancel')" />
            <button
              @click="onSubmit"
              class="bg-red-100 text-red-800 hover:bg-red-700 hover:text-white focus:ring-red-200 inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
              v-html="t('form.button.reverse')" />
          </section>
        </div>
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reversePayrolls.list.title')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard>
          <div class="grid grid-cols-6 gap-3">
            <div class="col-span-2">
              <DSelectFieldSearch
                :placeholder="t('form.button.select') + '...'"
                :label="t('reversePayrolls.fields.type')"
                v-model="processes"
                :options="filterOptions(itemsProcesses, searchProcesses)"
                @filter="searchProcesses = $event"
                @change="changeProcess" />
            </div>
            <div class="col-span-2 flex items-end">
              <DBtn
                @click.prevent="filterProcess"
                :icon="'SearchLgIcon'"
                v-html="t('form.button.search')" />
            </div>
            <div class="col-span-2 flex items-end justify-end">
              <DBtn
                color="white"
                @click.prevent="back"
                v-html="t('form.button.back')" />
            </div>
          </div>
        </DCard>
        <DCard v-for="item in itemsClosedPayrolls" :key="item.id">
          <div
            class="flex justify-between items-center"
            v-if="itemsClosedPayrolls.length > 0">
            <!-- dates -->
            <div>
              <p class="text-base font-medium">
                {{
                  `${formatDate(item.start_date)} - 
                  ${formatDate(item.end_date)}`
                }}
              </p>
              <span class="text-sm text-gray-400">
                {{
                  t('reversePayrolls.create.closedMessage', {
                    days: dayjs(item.created_at).diff(dayjs(dateCurrent), 'day')
                  })
                }}
              </span>
            </div>
            <!-- Employee -->
            <div class="text-base font-semibold">
              <p
                v-for="(employee, index) in item.employees
                  .split(',')
                  .slice(0, 1)"
                :key="index">
                {{ employee }}
                <span
                  v-if="item.employees.split(',').length > 1"
                  class="bg-blue-50 rounded-xl text-blue-600 py-1 px-2">
                  + {{ item.employees.split(',').length - 1 }}
                </span>
              </p>
            </div>
            <!-- concepts -->
            <div>
              <p class="text-base font-semibold">
                {{ `${item.total_concepts} ${t('fields.masterConcepts')}` }}
              </p>
            </div>
            <!-- net -->
            <div>
              <p class="text-base font-semibold text-gray-400">
                {{ t('reversePayrolls.fields.net') }}
                <span class="text-black">
                  {{ `${item.symbol}${item.neto}` }}</span
                >
              </p>
            </div>
            <div v-if="payrollStore.payroll?.country?.iso_code === 'MX'"
              class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
              :class="
                item.stamped_payroll_id
                  ? 'bg-green-100 text-green-600'
                  : 'bg-orange-100 text-orange-600'
              ">
              <div
                class="mr-2 w-1.5 h-1.5 rounded-full"
                :class="item.stamped_payroll_id ? 'bg-green-600' : 'bg-orange-600'"></div>
              {{
                item.stamped_payroll_id
                  ? t('processedPayrolls.list.successStampedPayroll')
                  : t('processedPayrolls.list.pendingStampedPayroll')
              }}
            </div>
            <!-- button -->
            <div
              @click="openModalReversePayroll(item)"
              class="p-2 rounded-lg bg-gray-100 cursor-pointer hover:bg-gray-300 text-black">
              <Trash03Icon />
            </div>
          </div>
        </DCard>
        <DCard
          v-if="isShowData"
          style="
            background-color: rgba(255, 237, 213, 10);
            border: solid 1px rgba(254, 215, 170, 10);
          ">
          <div class="inline-flex gap-2 items-center text-orange-600 text-sm">
            <InfoCircleIcon />
            <p>
              {{ t('reversePayrolls.create.noProcesses') }}
              <span class="font-bold">{{ nameProcess }}</span>
            </p>
          </div>
        </DCard>
      </div>
    </form>
  </div>
</template>
